import { getFunctions, httpsCallable } from 'firebase/functions';
import { initializeApp } from 'firebase/app';

// Initialize Firebase app for functions
const config = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
};

const app = initializeApp(config);
const functions = getFunctions(app);

/**
 * Employee Management Service
 * Handles all employee-related operations through Cloud Functions
 */
class EmployeeService {
  constructor() {
    // Initialize Cloud Function references
    this.createEmployeeFn = httpsCallable(functions, 'createEmployee');
    this.updateEmployeeFn = httpsCallable(functions, 'updateEmployee');
    this.deactivateEmployeeFn = httpsCallable(functions, 'deactivateEmployee');
    this.reactivateEmployeeFn = httpsCallable(functions, 'reactivateEmployee');
    this.getEmployeesFn = httpsCallable(functions, 'getEmployees');
  }

  /**
   * Create a new employee account
   * @param {Object} employeeData - Employee information
   * @param {string} employeeData.email - Employee email
   * @param {string} employeeData.password - Employee password
   * @param {string} employeeData.name - Employee name
   * @param {string} employeeData.displayName - Employee display name (optional)
   * @param {string} employeeData.employeeId - Unique employee ID
   * @param {string} employeeData.department - Employee department (optional)
   * @param {string} employeeData.position - Employee position (optional)
   * @param {string} employeeData.phoneNumber - Employee phone number (optional)
   * @returns {Promise<Object>} Result object with success status and employee ID
   */
  async createEmployee(employeeData) {
    try {
      console.log('Creating employee:', employeeData);

      // Validate required fields
      if (!employeeData.email || !employeeData.password || !employeeData.name || !employeeData.employeeId) {
        throw new Error('Email, password, name, and employee ID are required');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(employeeData.email)) {
        throw new Error('Invalid email format');
      }

      // Validate password strength
      if (employeeData.password.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      // Prepare data for Cloud Function
      const functionData = {
        email: employeeData.email.trim(),
        password: employeeData.password,
        employeeData: {
          name: employeeData.name.trim(),
          displayName: employeeData.displayName?.trim() || '',
          employeeId: employeeData.employeeId.trim(),
          department: employeeData.department?.trim() || '',
          position: employeeData.position?.trim() || '',
          phoneNumber: employeeData.phoneNumber?.trim() || ''
        }
      };

      const result = await this.createEmployeeFn(functionData);
      console.log('Employee created successfully:', result.data);

      return {
        success: true,
        employeeId: result.data.employeeId,
        message: result.data.message
      };

    } catch (error) {
      console.error('Error creating employee:', error);
      
      let errorMessage = 'Failed to create employee account';
      
      if (error.code === 'functions/already-exists') {
        errorMessage = error.message || 'Employee ID or email already exists';
      } else if (error.code === 'functions/invalid-argument') {
        errorMessage = error.message || 'Invalid employee data provided';
      } else if (error.code === 'functions/permission-denied') {
        errorMessage = 'You do not have permission to create employee accounts';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Update employee information
   * @param {string} employeeUid - Employee UID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Result object with success status
   */
  async updateEmployee(employeeUid, updateData) {
    try {
      console.log('Updating employee:', employeeUid, updateData);

      if (!employeeUid) {
        throw new Error('Employee UID is required');
      }

      // Filter out undefined and null values, but allow empty strings for optional fields
      const filteredData = {};
      Object.keys(updateData).forEach(key => {
        if (updateData[key] !== undefined && updateData[key] !== null) {
          // For displayName, allow empty strings (to clear the field)
          if (key === 'displayName') {
            filteredData[key] = typeof updateData[key] === 'string' ? updateData[key].trim() : updateData[key];
          } else if (updateData[key] !== '') {
            // For other fields, filter out empty strings
            filteredData[key] = typeof updateData[key] === 'string' ? updateData[key].trim() : updateData[key];
          }
        }
      });

      const result = await this.updateEmployeeFn({
        employeeUid,
        updateData: filteredData
      });

      console.log('Employee updated successfully:', result.data);

      return {
        success: true,
        message: result.data.message
      };

    } catch (error) {
      console.error('Error updating employee:', error);
      
      let errorMessage = 'Failed to update employee';
      
      if (error.code === 'functions/not-found') {
        errorMessage = 'Employee not found';
      } else if (error.code === 'functions/permission-denied') {
        errorMessage = 'You do not have permission to update employee accounts';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Deactivate employee account
   * @param {string} employeeUid - Employee UID
   * @returns {Promise<Object>} Result object with success status
   */
  async deactivateEmployee(employeeUid) {
    try {
      console.log('Deactivating employee:', employeeUid);

      if (!employeeUid) {
        throw new Error('Employee UID is required');
      }

      const result = await this.deactivateEmployeeFn({ employeeUid });
      console.log('Employee deactivated successfully:', result.data);

      return {
        success: true,
        message: result.data.message
      };

    } catch (error) {
      console.error('Error deactivating employee:', error);
      
      let errorMessage = 'Failed to deactivate employee';
      
      if (error.code === 'functions/not-found') {
        errorMessage = 'Employee not found';
      } else if (error.code === 'functions/permission-denied') {
        errorMessage = 'You do not have permission to deactivate employee accounts';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Reactivate employee account
   * @param {string} employeeUid - Employee UID
   * @returns {Promise<Object>} Result object with success status
   */
  async reactivateEmployee(employeeUid) {
    try {
      console.log('Reactivating employee:', employeeUid);

      if (!employeeUid) {
        throw new Error('Employee UID is required');
      }

      const result = await this.reactivateEmployeeFn({ employeeUid });
      console.log('Employee reactivated successfully:', result.data);

      return {
        success: true,
        message: result.data.message
      };

    } catch (error) {
      console.error('Error reactivating employee:', error);
      
      let errorMessage = 'Failed to reactivate employee';
      
      if (error.code === 'functions/not-found') {
        errorMessage = 'Employee not found';
      } else if (error.code === 'functions/permission-denied') {
        errorMessage = 'You do not have permission to reactivate employee accounts';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Get all employees
   * @returns {Promise<Object>} Result object with employees list
   */
  async getEmployees() {
    try {
      console.log('Fetching employees list...');

      const result = await this.getEmployeesFn();
      console.log('Employees fetched successfully:', result.data.employees?.length || 0, 'employees');

      return {
        success: true,
        employees: result.data.employees || []
      };

    } catch (error) {
      console.error('Error fetching employees:', error);
      
      let errorMessage = 'Failed to fetch employees';
      
      if (error.code === 'functions/permission-denied') {
        errorMessage = 'You do not have permission to view employee accounts';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage,
        employees: []
      };
    }
  }

  /**
   * Validate employee data before submission
   * @param {Object} employeeData - Employee data to validate
   * @returns {Object} Validation result with errors if any
   */
  validateEmployeeData(employeeData) {
    const errors = {};

    // Required fields validation
    if (!employeeData.name || employeeData.name.trim() === '') {
      errors.name = 'Name is required';
    }

    if (!employeeData.email || employeeData.email.trim() === '') {
      errors.email = 'Email is required';
    } else {
      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(employeeData.email)) {
        errors.email = 'Invalid email format';
      }
    }

    if (!employeeData.employeeId || employeeData.employeeId.trim() === '') {
      errors.employeeId = 'Employee ID is required';
    }

    if (!employeeData.password || employeeData.password === '') {
      errors.password = 'Password is required';
    } else if (employeeData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters long';
    }

    // Optional field validation
    if (employeeData.phoneNumber && employeeData.phoneNumber.trim() !== '') {
      // Basic phone number validation (Singapore format)
      const phoneRegex = /^[689]\d{7}$/;
      if (!phoneRegex.test(employeeData.phoneNumber.replace(/\s+/g, ''))) {
        errors.phoneNumber = 'Invalid phone number format (8 digits starting with 6, 8, or 9)';
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  /**
   * Format employee data for display
   * @param {Object} employee - Raw employee data
   * @returns {Object} Formatted employee data
   */
  formatEmployeeForDisplay(employee) {
    return {
      uid: employee.uid,
      name: employee.name || 'N/A',
      displayName: employee.displayName || '',
      email: employee.email || 'N/A',
      employeeId: employee.employeeId || 'N/A',
      department: employee.department || 'Not specified',
      position: employee.position || 'Not specified',
      phoneNumber: employee.phoneNumber || 'Not specified',
      isActive: employee.isActive !== undefined ? employee.isActive : true,
      createdAt: employee.createdAt ? new Date(employee.createdAt).toLocaleDateString() : 'N/A',
      updatedAt: employee.updatedAt ? new Date(employee.updatedAt).toLocaleDateString() : 'N/A'
    };
  }
}

// Create and export singleton instance
const employeeService = new EmployeeService();
export default employeeService;
